import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `promotion` domain — the five tabs of `/promotion`.
 *
 * Every claim in the copy was read out of the source, not out of the survey:
 * `apps/promo/src/repo/promo-calc.ts` (percent grants), `repo/rebate-calc.ts`
 * (bips rebates), `repo/turnover.ts` (wagering requirement), `repo/promotions.ts`
 * (claim lifecycle, coupons, summary) and `consumers/wallet-bet-debit.consumer.ts`
 * (which promotion fields are re-read live on every bet).
 */
export const promotionGuide: DomainGuide = {
  domain: 'promotion',
  pages: [
    {
      id: 'promotion.promotions',
      route: '/promotion#promotions',
      permission: 'promotion:read',
      status: 'live',
      fields: [
        { key: 'code', kind: 'text' },
        { key: 'kind', kind: 'status' },
        { key: 'period', kind: 'date' },
        {
          key: 'claims',
          kind: 'count',
          formula: 'claims = quotaUsedCount / maxClaimsTotal   (0 → ∞)',
        },
        { key: 'maxClaimsPerMember', kind: 'count' },
        {
          key: 'turnoverMultiplier',
          kind: 'count',
          formula: 'turnoverRequired = ceil(grantedAmount × turnoverMultiplier)',
        },
        { key: 'pct', kind: 'percent', formula: 'grant = deposit × pct / 100' },
        {
          key: 'defaultBips',
          kind: 'basis-points',
          formula: 'rebate = base × defaultBips / 10000',
        },
        { key: 'periodAnchor', kind: 'text' },
        { key: 'ratesEffectiveFrom', kind: 'date' },
        { key: 'expiryDays', kind: 'count' },
        { key: 'minTotalDeposit', kind: 'money-unsigned' },
        { key: 'minTotalTurnover', kind: 'money-unsigned' },
        { key: 'minDepositCount', kind: 'count' },
        { key: 'autoApprove', kind: 'status' },
        { key: 'status', kind: 'status' },
      ],
      cautionCount: 9,
      warningCount: 1,
    },
    {
      id: 'promotion.coupons',
      route: '/promotion#coupons',
      permission: 'promotion:read',
      status: 'live',
      fields: [
        { key: 'code', kind: 'text' },
        {
          key: 'usage',
          kind: 'count',
          formula: 'usage = usedCount / maxUses   (maxUses = 0 → ∞)',
        },
        { key: 'maxUses', kind: 'count' },
        { key: 'mode', kind: 'status' },
        { key: 'amountOverride', kind: 'money-unsigned' },
        {
          key: 'turnoverOverride',
          kind: 'count',
          formula: 'turnoverRequired = ceil(grantedAmount × turnoverOverride)',
        },
        { key: 'validity', kind: 'date' },
        { key: 'status', kind: 'status' },
      ],
      cautionCount: 7,
      warningCount: 1,
    },
    {
      id: 'promotion.claims',
      route: '/promotion#claims',
      permission: 'promotion:read',
      status: 'live',
      fields: [
        { key: 'createdAt', kind: 'date' },
        { key: 'member', kind: 'text' },
        { key: 'promotionTitle', kind: 'text' },
        { key: 'grantedAmount', kind: 'money-unsigned' },
        {
          key: 'turnoverProgress',
          kind: 'money-unsigned',
          formula: 'turnoverProgress = turnoverAchieved / turnoverRequired',
        },
        { key: 'status', kind: 'status' },
        { key: 'creditAttempted', kind: 'date' },
      ],
      cautionCount: 7,
      warningCount: 3,
    },
    {
      id: 'promotion.luckySpin',
      route: '/promotion#lucky-spin',
      permission: 'promotion:read',
      status: 'live',
      fields: [
        {
          key: 'depositPerSpin',
          kind: 'money-unsigned',
          formula: 'spinsGranted = floor((carry + deposit) / depositPerSpin)',
        },
        {
          key: 'turnoverMultiplier',
          kind: 'count',
          formula: 'turnoverRequired = ceil(prizeCash × turnoverMultiplier)',
        },
        { key: 'maxPerDay', kind: 'count' },
        { key: 'maxGrantsPerDay', kind: 'count' },
        { key: 'minTotalDeposit', kind: 'money-unsigned' },
        { key: 'minTotalTurnover', kind: 'money-unsigned' },
        { key: 'minDepositCount', kind: 'count' },
        { key: 'prizeWeight', kind: 'count' },
        { key: 'prizeOdds', kind: 'percent', formula: 'odds = weight / Σ weight' },
        { key: 'prizeCash', kind: 'money-unsigned' },
        { key: 'status', kind: 'status' },
      ],
      cautionCount: 9,
      warningCount: 1,
    },
    {
      id: 'promotion.report',
      route: '/promotion#report',
      permission: 'promotion:read',
      status: 'live',
      fields: [
        { key: 'totalClaims', kind: 'count' },
        {
          key: 'totalGranted',
          kind: 'money-unsigned',
          formula: 'totalGranted = Σ grantedAmount  (every status, rejected included)',
        },
        { key: 'totalForfeited', kind: 'money-unsigned' },
        {
          key: 'activeLockedBonus',
          kind: 'money-unsigned',
          formula:
            'activeLockedBonus = Σ grantedAmount WHERE status IN (active, approved) AND turnoverAchieved < turnoverRequired',
        },
        { key: 'byStatus', kind: 'count' },
        { key: 'byKind', kind: 'count' },
        { key: 'topPromotions', kind: 'money-unsigned' },
        { key: 'claimantGranted', kind: 'money-unsigned' },
        { key: 'claimantReason', kind: 'text' },
        { key: 'claimantActor', kind: 'text' },
      ],
      cautionCount: 7,
      warningCount: 0,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
